import { createSelector, createSlice } from '@reduxjs/toolkit'
import {
  baseReducers,
  getIdOrModelId,
  SBAPI,
  SBSelectRaw,
  SBState,
} from '@supplement-bacon/react-toolkit/utils'
import { initialQuery, TableParams } from '@supplement-bacon/sb-table-filters'
import { Policy } from '../../models/business/Policy'
import { policySchema } from '../../models/schema'
import {
  createPolicy as createPolicyAPI,
  deletePolicy as deletePolicyAPI,
  fetchPolicies,
  fetchPolicyById,
  updatePolicy as updatePolicyAPI,
} from '../../services/business/PolicyService'
import { AppDispatch, RootState } from '../store'

const initialState: SBState<Policy> = {
  isLoading: false,
  error: null,
  items: {},
  ids: [],
  selectedId: undefined,
  query: initialQuery,
}

const slice = createSlice({
  name: 'policy',
  initialState,
  reducers: baseReducers,
})

// Reducer
export default slice.reducer
export const {
  getItemsSuccess: getPoliciesSuccess,
  setQuery: setPolicyQuery,
  reset: resetPolicyState,
  resetQueryAndIds: resetPolicyQueryAndIds,
} = slice.actions

/**
 * Selectors
 */

const selectRawItems: SBSelectRaw<RootState, { [key: number]: Policy }> = (
  state: RootState
) => state[slice.name].items
const selectRawIds: SBSelectRaw<RootState, number[]> = (state: RootState) =>
  state[slice.name].ids
const selectRawSelectedId: SBSelectRaw<RootState, number | undefined> = (
  state: RootState
) => state[slice.name].selectedId

export const selectPolicies = () =>
  createSelector(
    [selectRawItems, selectRawIds],
    (items, ids) => ids.map((id) => items[id]).filter((i) => i)
    // Filter allow to return only non-null elements
  )
export const selectSelectedPolicy = () =>
  createSelector([selectRawItems, selectRawSelectedId], (items, id) =>
    id !== undefined ? items[id] : undefined
  )
export const selectPolicyById = (id: number) =>
  createSelector([selectRawItems], (items) =>
    items.hasOwnProperty(id) ? items[id] : undefined
  )
export const selectPoliciesByIds = (ids: number[]) =>
  createSelector([selectRawItems], (items) =>
    ids.filter((id) => items.hasOwnProperty(id)).map((id) => items[id])
  )

/**
 * Actions
 */

export const setSelectedPolicy =
  (policy: Policy | number) => async (dispatch: AppDispatch) =>
    dispatch(slice.actions.setSelectedId(getIdOrModelId<Policy>(policy)))

export const getPolicies = (params: TableParams) =>
  SBAPI.FetchPaginatedDispatched(
    fetchPolicies(params),
    [policySchema],
    slice.actions
  )

export const createPolicy = (policy: Policy) =>
  SBAPI.CreateDispatched(createPolicyAPI(policy), policySchema, slice.actions)

export const getPolicyWithId = (id: number) =>
  SBAPI.FetchDispatched<Policy>(
    fetchPolicyById(id),
    policySchema,
    slice.actions
  )

export const updatePolicy = (policy: Policy) =>
  SBAPI.UpdateDispatched<Policy>(updatePolicyAPI(policy), policy, slice.actions)

export const deletePolicy = (policy: Policy) =>
  SBAPI.DestroyDispatched<Policy>(
    deletePolicyAPI(policy),
    policy,
    slice.actions
  )
